import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBalanceScale, FaSearch, FaPhone, FaEnvelope,
  FaMapMarkerAlt, FaGlobe, FaExternalLinkAlt, FaArrowLeft
} from 'react-icons/fa';
import './CountyAttorneys.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CountyAttorneys = () => {
  const [attorneys, setAttorneys] = useState([]);
  const [groupedAttorneys, setGroupedAttorneys] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAttorneys();
  }, []);

  useEffect(() => {
    // Group attorneys by county
    const grouped = {};
    attorneys.forEach(attorney => {
      const county = attorney.county || 'Unknown';
      if (!grouped[county]) {
        grouped[county] = [];
      }
      grouped[county].push(attorney);
    });
    setGroupedAttorneys(grouped);
  }, [attorneys]);

  const fetchAttorneys = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/address-book/prosecuting-attorneys`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setAttorneys(data.data);
        setError('');
      } else {
        setError('Failed to load county attorneys');
      }
    } catch (err) {
      console.error('Error fetching county attorneys:', err);
      setError('Failed to load county attorneys');
    } finally {
      setLoading(false);
    }
  };

  const filteredCounties = Object.keys(groupedAttorneys).filter(county =>
    county.toLowerCase().includes(searchTerm.toLowerCase()) ||
    groupedAttorneys[county].some(attorney =>
      attorney.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attorney.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  ).sort();

  const getTitleBadge = (title) => {
    if (title.includes('County Attorney') && !title.includes('Deputy')) {
      return { label: 'County Attorney', color: '#c0392b' };
    } else if (title.includes('Chief Deputy')) {
      return { label: 'Chief Deputy', color: '#e67e22' };
    } else if (title.includes('Deputy')) {
      return { label: 'Deputy', color: '#16a085' };
    } else {
      return { label: 'Staff', color: '#7f8c8d' };
    }
  };

  if (loading) {
    return (
      <div className="county-attorneys-container">
        <div className="loading">Loading county attorneys...</div>
      </div>
    );
  }

  return (
    <div className="county-attorneys-container">
      {/* Header */}
      <div className="county-attorneys-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/address-book')}>
            <FaArrowLeft />
          </button>
          <FaBalanceScale size={32} />
          <div>
            <h1>Montana County Attorneys</h1>
            <p className="subtitle">{filteredCounties.length} county offices • {attorneys.length} total contacts</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="county-attorneys-search">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by county, name, or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Counties List */}
      <div className="counties-list">
        {filteredCounties.length === 0 ? (
          <div className="no-results">
            <FaBalanceScale size={48} />
            <p>No county attorneys found matching your search</p>
          </div>
        ) : (
          filteredCounties.map(county => {
            const countyAttorneys = groupedAttorneys[county];
            const mainAttorney = countyAttorneys.find(a =>
              a.title.includes('County Attorney') && !a.title.includes('Deputy')
            );
            const deputies = countyAttorneys.filter(a => a.title.includes('Deputy'));
            const staff = countyAttorneys.filter(a =>
              !a.title.includes('County Attorney') && !a.title.includes('Deputy')
            );

            return (
              <div key={county} className="county-office-card">
                <div className="county-header">
                  <h2>{county} County</h2>
                  <span className="county-stats">
                    {countyAttorneys.length} {countyAttorneys.length === 1 ? 'contact' : 'contacts'}
                  </span>
                </div>

                {mainAttorney && (
                  <div className="main-attorney">
                    <div className="attorney-info">
                      <div className="attorney-name-title">
                        <h3>{mainAttorney.name}</h3>
                        <span
                          className="title-badge"
                          style={{ backgroundColor: getTitleBadge(mainAttorney.title).color }}
                        >
                          {getTitleBadge(mainAttorney.title).label}
                        </span>
                      </div>
                      <p className="office-name">{mainAttorney.office_name}</p>
                    </div>

                    <div className="contact-details">
                      {mainAttorney.address_street && (
                        <div className="contact-item">
                          <FaMapMarkerAlt className="contact-icon" />
                          <span>
                            {mainAttorney.address_street}
                            {mainAttorney.address_city && `, ${mainAttorney.address_city}`}
                            {mainAttorney.address_state && `, ${mainAttorney.address_state}`}
                            {mainAttorney.address_zip && ` ${mainAttorney.address_zip}`}
                          </span>
                        </div>
                      )}

                      {mainAttorney.phone && (
                        <div className="contact-item">
                          <FaPhone className="contact-icon" />
                          <a href={`tel:${mainAttorney.phone}`}>{mainAttorney.phone}</a>
                        </div>
                      )}

                      {mainAttorney.email && (
                        <div className="contact-item">
                          <FaEnvelope className="contact-icon" />
                          <a href={`mailto:${mainAttorney.email}`}>{mainAttorney.email}</a>
                        </div>
                      )}

                      {mainAttorney.website && (
                        <div className="contact-item">
                          <FaGlobe className="contact-icon" />
                          <a
                            href={mainAttorney.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="website-link"
                          >
                            Website <FaExternalLinkAlt size={10} />
                          </a>
                        </div>
                      )}
                    </div>

                    {mainAttorney.notes && (
                      <div className="attorney-notes">
                        <small>{mainAttorney.notes}</small>
                      </div>
                    )}
                  </div>
                )}

                {deputies.length > 0 && (
                  <div className="staff-section">
                    <h4>Deputy County Attorneys ({deputies.length})</h4>
                    <div className="staff-list">
                      {deputies.map(deputy => (
                        <div key={deputy.id} className="staff-member">
                          <div className="staff-name-title">
                            <strong>{deputy.name}</strong>
                            <span
                              className="title-badge small"
                              style={{ backgroundColor: getTitleBadge(deputy.title).color }}
                            >
                              {getTitleBadge(deputy.title).label}
                            </span>
                          </div>
                          {deputy.phone && (
                            <div className="staff-contact">
                              <FaPhone size={12} />
                              <a href={`tel:${deputy.phone}`}>{deputy.phone}</a>
                            </div>
                          )}
                          {deputy.email && (
                            <div className="staff-contact">
                              <FaEnvelope size={12} />
                              <a href={`mailto:${deputy.email}`}>{deputy.email}</a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {staff.length > 0 && (
                  <div className="staff-section">
                    <h4>Support Staff ({staff.length})</h4>
                    <div className="staff-list">
                      {staff.map(member => (
                        <div key={member.id} className="staff-member">
                          <div className="staff-name-title">
                            <strong>{member.name}</strong>
                            <span className="staff-title">{member.title}</span>
                          </div>
                          {member.phone && (
                            <div className="staff-contact">
                              <FaPhone size={12} />
                              <a href={`tel:${member.phone}`}>{member.phone}</a>
                            </div>
                          )}
                          {member.email && (
                            <div className="staff-contact">
                              <FaEnvelope size={12} />
                              <a href={`mailto:${member.email}`}>{member.email}</a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CountyAttorneys;
